import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Trash } from 'iconoir-react';
import { deleteAccount } from '../../services/authService';
import Toaster from '../common/Toaster';
import { useAuth } from '../../contexts/AuthContext';

/**
 * DangerZone — account deletion settings panel.
 *
 * Apr-20. Mirrors the mobile Profile screen's Delete Account flow
 * (2connect-mobile-app/src/app/(tabs)/profile.tsx). Double-confirmation
 * with typed "DELETE" gate prevents accidental taps; Alert 1 explains the
 * 30-day window, Alert 2 requires explicit typed confirmation.
 *
 * Required by Apple App Review Guideline 5.1.1(v) + Google Play account-
 * deletion policy. Google Play specifically requires a reachable deletion
 * URL (data-deletion-url in Data Safety form) — this page at
 * /settings?tab=danger-zone satisfies it.
 *
 * Backend: DELETE /users/me (reciprocity-backend). Soft-deletes
 * immediately; hard-delete sweeper at 30 days.
 */
const DangerZone: React.FC = () => {
  const { logout } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [scheduledDate, setScheduledDate] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => deleteAccount(),
    onSuccess: async (response: any) => {
      const scheduled = response?.scheduled_hard_delete
        ? new Date(response.scheduled_hard_delete).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        : null;
      setScheduledDate(scheduled);
      toast.custom(t => (
        <Toaster
          message={`Your account has been deactivated. Full deletion ${
            scheduled ? `will complete on ${scheduled}` : 'will complete in 30 days'
          }. Check your email for reactivation instructions.`}
          type='success'
          id={t.id}
        />
      ));
      // Log out + hard redirect (clears all in-memory state + tokens).
      // Wait ~2s so user sees the confirmation toast before redirect.
      setTimeout(() => {
        logout();
      }, 2000);
    },
    onError: (error: any) => {
      toast.custom(t => (
        <Toaster
          message={
            error?.response?.data?.message ||
            error?.message ||
            'Could not delete account. Please try again or contact support@2connect.ai.'
          }
          type='error'
          id={t.id}
        />
      ));
    },
  });

  const handleOpenConfirm = () => {
    setShowConfirm(true);
    setConfirmText('');
  };

  const handleCancel = () => {
    setShowConfirm(false);
    setConfirmText('');
  };

  const handleDelete = () => {
    if (confirmText !== 'DELETE') return;
    deleteMutation.mutate();
  };

  const isDeleting = deleteMutation.isPending;
  const canDelete = confirmText === 'DELETE' && !isDeleting;

  return (
    <div className='max-w-[33.625rem]'>
      <div className='mb-6'>
        <h5 className='text-heading-500 !font-medium'>Delete account</h5>
        <p className='mb-0 text-sm text-body-400 mt-1'>
          Permanently remove your 2Connect Professional account and associated data.
        </p>
      </div>

      <div className='border border-red-200 bg-red-50 rounded-xl p-5'>
        <div className='flex items-start gap-3 mb-4'>
          <Trash className='w-6 h-6 text-red-500 flex-shrink-0 mt-0.5' />
          <div>
            <h6 className='!font-medium text-body-500 !mb-1'>
              Delete my account
            </h6>
            <p className='text-sm text-body-400 mb-0'>
              Your account will be deactivated immediately. Your profile,
              matches, messages, and other personal data will be permanently
              deleted 30 days later. You can reactivate within the 30-day
              window by replying to the confirmation email we send you.
            </p>
          </div>
        </div>

        {!showConfirm && !scheduledDate && (
          <button
            type='button'
            onClick={handleOpenConfirm}
            className='bg-red-500 hover:bg-red-600 text-white font-medium px-5 py-2.5 rounded-lg transition-colors'
          >
            Delete my account
          </button>
        )}

        {showConfirm && !scheduledDate && (
          <div className='mt-4 border-t border-red-200 pt-4'>
            <p className='text-sm text-body-500 mb-3 !font-medium'>
              Type <span className='font-mono text-red-600'>DELETE</span> below
              to confirm. This cannot be undone after the 30-day window
              expires.
            </p>
            <input
              type='text'
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder='Type DELETE to confirm'
              className='w-full px-4 py-2.5 border border-red-300 rounded-lg font-mono mb-3 focus:outline-none focus:ring-2 focus:ring-red-400'
              autoFocus
              disabled={isDeleting}
            />
            <div className='flex gap-3'>
              <button
                type='button'
                onClick={handleCancel}
                disabled={isDeleting}
                className='bg-white border border-gray-300 hover:bg-gray-50 text-body-500 font-medium px-5 py-2.5 rounded-lg transition-colors disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleDelete}
                disabled={!canDelete}
                className='bg-red-500 hover:bg-red-600 text-white font-medium px-5 py-2.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed'
              >
                {isDeleting ? 'Deleting…' : 'Permanently delete'}
              </button>
            </div>
          </div>
        )}

        {scheduledDate && (
          <div className='mt-4 border-t border-red-200 pt-4'>
            <p className='text-sm text-body-500 mb-1 !font-medium'>
              Account deactivated.
            </p>
            <p className='text-sm text-body-400 mb-0'>
              Full deletion will complete on {scheduledDate}. Signing out…
            </p>
          </div>
        )}
      </div>

      <div className='mt-6 text-sm text-body-400'>
        <p className='mb-1'>Questions or need help?</p>
        <a
          href='mailto:support@2connect.ai'
          className='text-theme-blue-600 hover:underline'
        >
          support@2connect.ai
        </a>
      </div>
    </div>
  );
};

export default DangerZone;
